// =============================================================================
// protocol.ts — O contrato Socket.IO entre servidor e cliente: nomes de evento,
// formato de snapshot (compacto, em tuplas) e todos os payloads tipados.
// Documentação narrativa em docs/PROTOCOL.md.
// =============================================================================
import type { DifficultyId } from './difficulty';
import type { EleicaoStartInfo } from './eleicao';
import type { DossieState, MatchFeats } from './feitos';
import type { PlayerMetaState } from './meta';

// -----------------------------------------------------------------------------
// Nomes dos eventos Socket.IO
// -----------------------------------------------------------------------------
export const MSG = {
  // Cliente -> Servidor
  ROOM_CREATE: 'room:create',
  ROOM_JOIN: 'room:join',
  ROOM_LIST: 'room:list',
  ROOM_RECONNECT: 'room:reconnect',
  ROOM_LEAVE: 'room:leave',     // botão VOLTAR do comitê: sai da chapa (só em lobby)
  LOBBY_READY: 'lobby:ready',
  LOBBY_START: 'lobby:start',
  LOBBY_CHAR: 'lobby:char',
  LOBBY_DIFFICULTY: 'lobby:difficulty',   // só o host escolhe (validado no servidor)
  INPUT: 'input',
  UPGRADE_PICK: 'upgrade:pick',
  URNA_ACTION: 'urna:action',   // poderes da mesa diretora (issue #100): reroll/banish/skip
  PAUSE_TOGGLE: 'game:pause',   // Esc no modo solo: pausa/retoma (servidor valida)
  GAME_QUIT: 'game:quit',       // botão do recesso no modo solo: encerra a partida
  INFINITE_START: 'game:infinite', // botão MODO INFINITO da tela de parabéns (issue #85)
  LOBBY_REMATCH: 'lobby:rematch',  // botão REELEIÇÃO do game over (issue #91): sala volta ao lobby
  ELEICAO_START: 'eleicao:start',  // Eleição do Dia/da Semana (#101/#97): sala solo com seed
  PING: 'ping',             // ida-e-volta pra medir latência (ack vazio do servidor)
  // Easter egg (código secreto digitado no teclado durante a partida)
  EASTER_CODE: 'game:easter',

  // Servidor -> Cliente
  LOBBY_UPDATE: 'lobby:update',
  GAME_START: 'game:start',
  STATE: 'state',
  EVENT: 'event',           // eventos pontuais (bordões, boss, urna, morte...)
  MATCH_CLEARED: 'game:cleared', // apuração vencida (#85): parabéns + SAIR/MODO INFINITO
  GAME_OVER: 'game:over',
  REMATCH: 'game:rematch',  // REELEIÇÃO aceita (#91): todo mundo volta pro comitê da mesma sala
  META_CREDIT: 'meta:credit',   // crédito de meta-progressão do ANÔNIMO (#90/#94):
                                // o navegador soma no perfil do localStorage
} as const;

// Tipos de evento pontuais dentro de MSG.EVENT
export const EV = {
  QUOTE: 'quote',               // inimigo/boss soltando bordão
  BOSS_SPAWN: 'bossSpawn',
  BOSS_DEATH: 'bossDeath',
  URNA_OFFER: 'urnaOffer',      // 3 opções de upgrade na Urna Hackeada (privado)
  URNA_RESULT: 'urnaResult',
  URNA_SKIP: 'urnaSkip',        // urna fechou sem voto (privado): abstenção aceita (issue #100,
                                // compensa com cura ou pontos) ou urna esvaziada por cassação
  LEVEL_UP: 'levelUp',          // broadcast: a chapa subiu de nível
  PLAYER_DOWNED: 'playerDowned',
  PLAYER_REVIVED: 'playerRevived',
  PLAYER_ZOMBIFIED: 'playerZombified',
  WAVE_CLEAR: 'waveClear',      // Impeachment Express limpou a onda
  CASINO: 'casino',             // mala aberta: roleta do Cassino da Maleta (CPI da BET)
  FREEZE_SELF: 'freezeSelf',    // Decisão do STF congelou o próprio jogador
  FORO: 'foro',                 // Foro Privilegiado ativou a imunidade
  FX: 'fx',                     // efeito visual pontual (cone, onda, explosão...)
  PLAYER_LEFT: 'playerLeft',
  PLAYER_BACK: 'playerBack',
  INFINITE_ON: 'infiniteOn',    // alguém apertou MODO INFINITO: fecha o card em todo mundo
  CONQUISTA: 'conquista',       // privado: Promessa de Campanha cumprida (#99) — toast
} as const;

// Estados possíveis de um jogador dentro da partida
export const PSTATE = {
  ALIVE: 0,
  DOWNED: 1,   // sangrando no chão, esperando revive
  ZOMBIE: 2,   // demorou demais: virou zumbi político controlado pelo servidor
  DEAD: 3,     // zumbi abatido — só espectador
} as const;
export type PlayerState = (typeof PSTATE)[keyof typeof PSTATE];

// Flags binárias por inimigo no snapshot
export const EFLAG = {
  FROZEN: 1,
  ROOTED: 2,
  ELITE: 4,
  BOSS: 8,
  GHOST: 16,
} as const;

// -----------------------------------------------------------------------------
// Input do cliente (25Hz, com número de sequência p/ ack e anti-replay)
// -----------------------------------------------------------------------------
export interface InputPayload {
  seq: number;
  mx: number;
  my: number;
}

// -----------------------------------------------------------------------------
// Snapshot (12.5Hz). Entidades em tuplas compactas pra economizar banda.
// -----------------------------------------------------------------------------
// maxHp viaja no snapshot pro cliente converter delta de hpFrac em dano real
// (números de dano estilo RPG desenhados localmente, sem evento por hit)
export type EnemySnap = [id: number, typeIdx: number, x: number, y: number, hpFrac: number, flags: number, maxHp: number];
export type AllySnap = [id: number, kind: string, x: number, y: number];
export type ProjSnap = [id: number, kind: string, x: number, y: number];
export type GemSnap = [id: number, x: number, y: number, value: number];
// Maço de Caixa 2 no chão (issues #90/#94): moeda persistente de meta-progressão
export type CoinSnap = [id: number, x: number, y: number, value: number];
export type ChestSnap = [id: number, x: number, y: number];
export type ZoneSnap = [id: number, kind: string, x: number, y: number, radius: number, frac: number];
export type WeaponSnap = [id: string, lvl: number, kills: number];

export interface PlayerSnap {
  id: string;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  st: PlayerState;
  face: number;
  spd: number;          // velocidade efetiva (p/ predição no cliente)
  k: number;            // kills
  c2: number;           // Caixa 2 coletado na partida (contador do HUD)
  wp: WeaponSnap[];
  rev: number;          // progresso de revive (0..1) quando caído
  bleed: number;        // segundos até virar zumbi quando caído
  frz: 0 | 1;           // congelado/preso
  inv: 0 | 1;           // invulnerável
  con: 0 | 1;           // conectado
}

export interface PauseInfo {
  names: string[];      // quem está votando na urna
  left: number;         // segundos até o voto compulsório
  manual?: boolean;     // recesso via Esc (só existe em partida solo)
}

export interface BossBarInfo {
  id: number;
  type: string;
  hp: number;
  maxHp: number;
}

export interface Snapshot {
  t: number;
  pause: PauseInfo | null;
  lvl: number;          // nível da CHAPA (compartilhado)
  xp: number;
  xpNeed: number;
  players: PlayerSnap[];
  enemies: EnemySnap[];
  allies: AllySnap[];
  projs: ProjSnap[];
  gems: GemSnap[];
  coins: CoinSnap[];
  chests: ChestSnap[];
  zones: ZoneSnap[];
  boss: BossBarInfo | null;
  ack?: number;         // último input seq processado (por jogador)
}

// -----------------------------------------------------------------------------
// Lobby / sala
// -----------------------------------------------------------------------------
export type RoomState = 'lobby' | 'playing' | 'ended';

export interface LobbyPlayer {
  id: string;
  name: string;
  charId: string;
  skinId: string;   // skin escolhida (issue #24); 'oficial' = visual padrão
  ready: boolean;
  connected: boolean;
}

// Payload de lobby:char — troca de personagem e/ou skin no comitê
export interface LobbyCharPayload {
  charId: string;
  skinId?: string;
}

export interface LobbySnapshot {
  code: string;
  hostId: string | null;
  state: RoomState;
  difficulty: DifficultyId;
  players: LobbyPlayer[];
}

export interface OpenRoomInfo {
  code: string;
  players: number;
  max: number;
  host: string;
  difficulty: DifficultyId;   // o mural mostra o tamanho da encrenca antes de entrar
}

// Respostas com callback (ack) das mensagens de sala
export type AckResponse<T> = ({ ok: true } & T) | { ok: false; error: string };

export interface JoinedRoom {
  code: string;
  playerId: string;
  token: string;
  hostId: string | null;
}
export type JoinAck = AckResponse<JoinedRoom>;
export type RoomListAck = AckResponse<{ rooms: OpenRoomInfo[] }>;
export type ReconnectAck = AckResponse<{
  playerId: string;
  hostId: string | null;
  state: RoomState;               // 'ended' = game over esperando REELEIÇÃO (#91)
  resume: ResumePayload | null;   // só em partida ('playing')
  // Sala 'ended': o payload do game over guardado no servidor — o cliente que
  // caiu (ou deu F5) na tela de fim de mandato reconstrói a tela e mantém o
  // botão REELEIÇÃO vivo em vez de perder a sessão (issue #91)
  gameOver: GameOverPayload | null;
}>;

// REELEIÇÃO (issue #91): pedido de rematch — a sala 'ended' volta ao lobby
// com o mesmo código e a mesma chapa; quem pediu já entra pronto.
export type RematchAck = { ok: true } | { ok: false; error: string };

// Broadcast de rematch aceito: todos os clientes voltam pro comitê
export interface RematchPayload {
  by: string;      // nome de urna de quem apertou REELEIÇÃO
  byId: string;    // id do jogador (o cliente dele já se marca pronto)
}

// -----------------------------------------------------------------------------
// Payloads cliente -> servidor (o formato REAL no fio). O servidor NÃO confia
// neles: toda entrada continua chegando como `unknown` e validada à mão —
// estes tipos servem ao emit tipado do cliente e como documentação.
// -----------------------------------------------------------------------------
export interface RoomCreatePayload {
  name: string;
  charId: string;
  skinId: string;
  meta: PlayerMetaState;   // perfil local do ANÔNIMO (#90/#94); logado, o servidor ignora
}

export interface RoomJoinPayload extends RoomCreatePayload {
  code: string;
}

export interface RoomReconnectPayload {
  code: string;
  token: string;
}

// Sem skinId: o cliente nunca envia o traje aqui — o tipo documenta o fio real
// (o servidor até tenta ler um skinId que não chega; ver SocketGateway)
export interface EleicaoStartPayload {
  mode: string;
  name: string;
  charId: string;
}

export interface UpgradePickPayload {
  idx: number;
}

// -----------------------------------------------------------------------------
// Partida: início, retomada e fim
// -----------------------------------------------------------------------------
export interface GameStartPlayer {
  id: string;
  name: string;
  charId: string;
  skinId: string;   // skin escolhida no lobby (issue #24)
}

export interface GameStartPayload {
  arena: { w: number; h: number };
  duration: number;
  difficulty: DifficultyId;
  players: GameStartPlayer[];
  // Eleição com seed (#101/#97): período, modificadores do dia e se pontua.
  // Ausente/null = partida normal.
  eleicao?: EleicaoStartInfo | null;
}

// Payload completo para reconexão: reconstrói o cliente do zero
export interface ResumePayload {
  you: string;
  start: GameStartPayload;
  snapshot: Snapshot;
  pendingOffer: UrnaChoiceDisplay[] | null;   // urna reaberta na volta
  offerTime: number;
  offerCharges: UrnaCharges | null;           // cargas restantes da mesa diretora (#100)
  cleared: MatchClearedPayload | null;        // caiu durante a tela de parabéns (#85)?
}

// Apuração vencida (issue #85): estatísticas da chapa na tela de parabéns
export interface MatchClearedWeapon {
  id: string;
  lvl: number;
}

export interface MatchClearedStats {
  id: string;
  name: string;
  charId: string;
  skinId: string;
  kills: number;
  score: number;                      // pontuação do ranking (issue #88)
  revives: number;                    // aliados reanimados (santinho, issue #96)
  zombified: boolean;                 // virou zumbi político em algum momento
  weapons: MatchClearedWeapon[];
  upgrades: Record<string, number>;   // upgrade -> vezes escolhido na urna
  votosGanhos: number;                // votos vitalícios creditados na conta (#90)
  caixa2Ganhos: number;               // Caixa 2 creditado na conta (#94)
  conquistas?: string[];              // Promessas de Campanha cumpridas NESTA run (#99)
  dossies?: DossieState[];            // pastas de dossiê pós-crédito (#92)
}

export interface MatchClearedPayload {
  frase: string;
  timeSurvived: number;
  level: number;                      // nível da chapa (compartilhado)
  stats: MatchClearedStats[];
  evolutions: string[];               // armas supremas alcançadas pela chapa (#98)
}

export interface GameOverStats {
  id: string;
  name: string;
  charId: string;
  skinId: string;
  kills: number;
  level: number;
  score: number;          // pontuação do ranking (issue #88): abates + tempo vivo
  revives: number;        // aliados reanimados (títulos do santinho, issue #96)
  zombified: boolean;     // virou zumbi político em algum momento ("O Vira-Casaca")
  votosGanhos: number;    // votos vitalícios creditados na conta (#90)
  caixa2Ganhos: number;   // Caixa 2 creditado na conta (#94)
  conquistas?: string[];  // Promessas de Campanha cumpridas NESTA run (#99)
  dossies?: DossieState[]; // pastas de dossiê pós-crédito (#92) — tela de game over
}

export interface GameOverPayload {
  victory: boolean;
  infinite: boolean;      // a derrota veio no MODO INFINITO (apuração já vencida)
  frase: string;
  timeSurvived: number;
  stats: GameOverStats[];
  evolutions: string[];   // armas supremas alcançadas pela chapa (#98)
}

// Crédito de meta-progressão (#90/#94) enviado ao jogador ANÔNIMO: o perfil
// dele mora no localStorage do navegador, então o servidor manda o delta
// ganho na partida e o cliente soma e guarda (logado, o crédito vai direto
// pro Postgres e esta mensagem não é enviada).
export interface MetaCreditPayload {
  votos: number;
  caixa2: number;
  // Feitos da participação (#99/#92/#95): o cliente anônimo funde no perfil
  // do localStorage com as MESMAS funções puras de shared/feitos.ts
  feitos?: MatchFeats;
}

// -----------------------------------------------------------------------------
// Urna Eletrônica Hackeada
// -----------------------------------------------------------------------------
export type UrnaChoiceKind = 'weapon' | 'upgrade';

export interface UrnaChoice {
  kind: UrnaChoiceKind;
  id: string;
  lvl?: number;
  weight: number;
  golden?: boolean;   // Emenda de Relator (issue #93): oferta dourada turbinada
}

// O cliente resolve o ícone pixel art localmente por (kind, id) — nada de
// emoji no protocolo (issue #31)
export interface UrnaChoiceDisplay {
  kind: UrnaChoiceKind;
  id: string;
  lvl?: number;
  nome: string;
  desc: string;
  golden?: boolean;   // card dourado "Emenda de Relator"
}

// Poderes da mesa diretora (issue #100): cargas restantes de cada ação,
// POR JOGADOR, decididas e validadas no servidor
export type UrnaActionKind = 'reroll' | 'banish' | 'skip';

export interface UrnaCharges {
  reroll: number;   // Recontagem de Votos
  banish: number;   // Cassação de Mandato
  skip: number;     // Abstenção
}

export interface UrnaActionPayload {
  action: UrnaActionKind;
  idx?: number;     // banish: índice da opção cassada (0..2)
}

// -----------------------------------------------------------------------------
// Eventos pontuais (enviados em LOTE por tick; urna e avisos vão avulsos)
// -----------------------------------------------------------------------------
export type FxKind = 'cone' | 'wave' | 'nova' | 'freeze' | 'zoneBlast' | 'steal' | 'pix';

export interface FxEvent {
  type: typeof EV.FX;
  fx: FxKind;
  x: number;
  y: number;
  radius?: number;
  angle?: number;
  range?: number;
  arc?: number;
  hostile?: boolean;
  kind?: string;        // tipo da zona no zoneBlast
}

export type GameEvent =
  | { type: typeof EV.QUOTE; text: string; x: number; y: number; boss: boolean }
  | { type: typeof EV.BOSS_SPAWN; boss: string; intro: string }
  | { type: typeof EV.BOSS_DEATH; boss: string; by?: string }
  | { type: typeof EV.URNA_OFFER; choices: UrnaChoiceDisplay[]; time: number; charges: UrnaCharges }
  | { type: typeof EV.URNA_RESULT; choice: UrnaChoice }
  // Abstenção: `heal` (vivo) OU `pontos` (caído — cura não sobrevive ao revive).
  // `empty` = urna esvaziada por cassação (fechou sem voto e sem gastar carga de skip)
  | { type: typeof EV.URNA_SKIP; heal: number; pontos: number; empty: boolean }
  | { type: typeof EV.LEVEL_UP; level: number }
  | { type: typeof EV.PLAYER_DOWNED; playerId: string; name: string }
  | { type: typeof EV.PLAYER_REVIVED; playerId: string; by?: string }
  | { type: typeof EV.PLAYER_ZOMBIFIED; playerId: string; kind: string; name: string }
  | { type: typeof EV.WAVE_CLEAR; by: string; wiped: number }
  // evoFrom/evoInto (#98): presentes quando o prêmio é o GOLPE DE MESTRE —
  // qual arma evolui e em qual arma suprema ela vira (ids de shared/weapons.ts).
  // evoName: nome de quem cumpriu a receita (nem sempre é quem abriu a mala)
  | { type: typeof EV.CASINO; playerId: string; name: string; prize: string; evoFrom?: string; evoInto?: string; evoName?: string; x: number; y: number }
  | { type: typeof EV.FREEZE_SELF; playerId: string; motivo: string }
  | { type: typeof EV.FORO; playerId: string }
  | { type: typeof EV.PLAYER_LEFT; playerId: string; name: string }
  | { type: typeof EV.PLAYER_BACK; playerId: string; name: string }
  | { type: typeof EV.INFINITE_ON; by: string }
  | { type: typeof EV.CONQUISTA; id: string; nome: string; desc: string; votos: number }
  | FxEvent;

// -----------------------------------------------------------------------------
// Mapas nome do evento -> payload: amarram cada MSG.* ao seu tipo nas duas
// direções. O cliente tipa on/emit por aqui; no servidor SÓ a saída usa o
// mapa (GameHost/RoomTransport) — a entrada segue como `unknown` validado à
// mão (servidor não confia em ninguém, tipo TSE).
// -----------------------------------------------------------------------------
export interface ServerToClientPayloads {
  [MSG.LOBBY_UPDATE]: LobbySnapshot;
  [MSG.GAME_START]: GameStartPayload;
  [MSG.STATE]: Snapshot;
  // Eventos do tick vão em LOTE (array); urna e avisos de sala chegam avulsos
  [MSG.EVENT]: GameEvent | GameEvent[];
  [MSG.MATCH_CLEARED]: MatchClearedPayload;
  [MSG.GAME_OVER]: GameOverPayload;
  [MSG.REMATCH]: RematchPayload;
  [MSG.META_CREDIT]: MetaCreditPayload;
}

// `undefined` = mensagem sem payload (viaja só o nome do evento e/ou o ack)
export interface ClientToServerPayloads {
  [MSG.ROOM_CREATE]: RoomCreatePayload;
  [MSG.ROOM_JOIN]: RoomJoinPayload;
  [MSG.ROOM_LIST]: Record<string, never>;
  [MSG.ROOM_RECONNECT]: RoomReconnectPayload;
  [MSG.ROOM_LEAVE]: undefined;
  [MSG.LOBBY_READY]: boolean;
  [MSG.LOBBY_START]: undefined;
  // O servidor ainda tolera o formato antigo (charId puro) de cliente em cache
  [MSG.LOBBY_CHAR]: LobbyCharPayload;
  [MSG.LOBBY_DIFFICULTY]: string;
  [MSG.INPUT]: InputPayload;
  [MSG.UPGRADE_PICK]: UpgradePickPayload;
  [MSG.URNA_ACTION]: UrnaActionPayload;
  [MSG.PAUSE_TOGGLE]: undefined;
  [MSG.GAME_QUIT]: undefined;
  [MSG.INFINITE_START]: undefined;
  [MSG.LOBBY_REMATCH]: Record<string, never>;
  [MSG.ELEICAO_START]: EleicaoStartPayload;
  [MSG.PING]: undefined;
  [MSG.EASTER_CODE]: { code: string };
}

// Acks das mensagens que respondem via callback; `void` = ack vazio (só sinal)
export interface ClientToServerAcks {
  [MSG.ROOM_CREATE]: JoinAck;
  [MSG.ROOM_JOIN]: JoinAck;
  [MSG.ROOM_LIST]: RoomListAck;
  [MSG.ROOM_RECONNECT]: ReconnectAck;
  [MSG.ELEICAO_START]: JoinAck;
  [MSG.LOBBY_REMATCH]: RematchAck;
  [MSG.GAME_QUIT]: void;
  [MSG.PING]: void;
}
